import debug from 'debug';

type CreateLoggerOptions = {
  /** defaults to 'log' */
  level?: 'log' | 'info' | 'warn' | 'error';
};

/**
 * Instantiate a debug log.
 *
 * ```tsx
 * const log = createLogger('MyComponent');
 * const warn = createLogger('MyComponent', { level: 'warn' });
 *
 * warn('some message');
 * log(obj);
 * //  ^ logs "MyComponent > Object" (requiring a click to see into obj)
 * log('%o', obj);
 * //   ^ a better experience, aligned with console.log
 * ```
 *
 * ### Configure which logs are shown at runtime
 *
 * In devtools:
 *
 * ```js
 * // all
 * localStorage.debug = '*'
 *
 * // logs registered under the 'widgets' namespace
 * localStorage.debug = 'widgets:*,-not_this'
 * ```
 *
 * Via environment variables:
 *
 * ```sh
 * # only useful with namespaces; this will activate vite, babel, connect...
 * DEBUG='my-project:*'
 * ```
 *
 * https://github.com/debug-js/debug
 */
export const createLogger = (
  namespace: string,
  options: CreateLoggerOptions = {},
) => {
  const log = debug(namespace);
  // prefer console.log to console.error by default
  // > By default debug will log to stderr
  // https://github.com/debug-js/debug?tab=readme-ov-file#output-streams
  log.log = console[options?.level ?? 'log'].bind(console);
  return log;
};
